import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useNav } from '../lib/nav'
import * as Sentry from '@sentry/react'
import { Page, TopBar } from '../components/Shell'
import { MoneyRow, InputRow, KV, SegRow } from '../components/Controls'
import { I } from '../components/Icons'
import { Skeleton, Empty } from '../components/Skeleton'
import { useStore } from '../state/store'
import { createConsignor, syncConsignors, updateConsignor } from '../lib/api'
import { consignorName, FEE_OPTIONS, feeChoice } from '../lib/types'
import type { Consignor, PayoutMethod } from '../lib/types'
import { Working } from '../components/StarTick'

const blank = { first_name: '', last_name: '', phone: '', email: '', default_split: 80, feeChoice: '20', feeOther: '', payout_method: null as PayoutMethod | null, payout_handle: '', unsold_fate: 'return' as 'return' | 'donate', agreement: 'signed' as 'signed' | 'not_yet' }

export function Consignors() {
  const nav = useNav()
  const [sp] = useSearchParams()
  const picking = sp.get('pick') === '1'
  const { consignors, setDraft, refresh, ready } = useStore()
  // Consignify is the record: pull its list when this screen opens so a consignor added there minutes ago is here.
  useEffect(() => { syncConsignors().then((r) => { if (r.added || r.updated) refresh() }).catch(() => {}) }, [])  // eslint-disable-line react-hooks/exhaustive-deps
  const [q, setQ] = useState('')
  const [sel, setSel] = useState<string>(picking ? '' : (consignors[0]?.id ?? ''))
  const [mode, setMode] = useState<'detail' | 'new' | 'edit'>(picking && consignors.length === 0 ? 'new' : 'detail')
  const [nc, setNc] = useState({ ...blank })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const list = consignors.filter((c) => !q || `${c.first_name} ${c.last_name} ${c.phone}`.toLowerCase().includes(q.toLowerCase()))
  const cur = consignors.find((c) => c.id === sel)

  const ret = sp.get('return')
  const use = (c: Consignor) => {
    if (ret) { try { sessionStorage.setItem('starchive.pick.consignor', c.id) } catch { /* ignore */ } nav(ret); return }
    const fee = 100 - c.default_split
    setDraft({ consignorId: c.id, fee: feeChoice(fee), feeOther: String(fee) })
    nav('/intake')
  }
  const pick = (c: Consignor) => { if (picking) use(c); else { setSel(c.id); setMode('detail') } }

  const ncOk = nc.first_name.trim() && nc.last_name.trim() && nc.phone.trim() && nc.default_split > 0 && nc.default_split < 100
  const save = async () => {
    setBusy(true); setErr('')
    try {
      const { feeChoice: _fc, feeOther: _fo, ...body } = nc
      const c = mode === 'edit' && cur ? await updateConsignor(cur.id, body, null) : await createConsignor(body, null)
      await refresh()
      setNc({ ...blank })
      if (picking && mode !== 'edit') use(c); else { setSel(c.id); setMode('detail') }
    } catch (e) { Sentry.captureException(e); setErr((e as Error).message) } finally { setBusy(false) }
  }
  const startEdit = () => {
    if (!cur) return
    const fee = 100 - cur.default_split
    setNc({ ...blank, first_name: cur.first_name, last_name: cur.last_name, phone: cur.phone ?? '', email: cur.email ?? '', default_split: cur.default_split, feeChoice: feeChoice(fee), feeOther: String(fee), payout_method: cur.payout_method, payout_handle: cur.payout_handle ?? '', unsold_fate: cur.unsold_fate, agreement: cur.agreement })
    setErr(''); setMode('edit')
  }

  return (
    <Page
      bar={picking ? <TopBar back="Intake" backTo="/intake" title="Choose consignor" /> : <TopBar back="Home" backTo="/" title="Consignors" caption={`${consignors.length} consignors`} />}
      aside={
        <>
          <div className="aside__body">
            {mode === 'new' || mode === 'edit' ? (
              <>
                <div className="section-title" style={{ padding: '16px 20px 4px' }}>{mode === 'edit' ? `Edit ${consignorName(cur!)}` : 'New consignor'}</div>
                <div className="hint" style={{ padding: '0 20px 6px' }}>Contact</div>
                <div className="card" style={{ margin: '0 20px' }}>
                  <InputRow id="ncf" label="First name" value={nc.first_name} onChange={(v) => setNc({ ...nc, first_name: v })} placeholder="First" />
                  <InputRow id="ncl" label="Last name" value={nc.last_name} onChange={(v) => setNc({ ...nc, last_name: v })} placeholder="Last" />
                  <InputRow id="ncp" label="Phone" value={nc.phone} onChange={(v) => setNc({ ...nc, phone: v })} placeholder="(212) 555-0100" tel />
                  <InputRow id="nce" label="Email" value={nc.email} onChange={(v) => setNc({ ...nc, email: v })} placeholder="Optional" email />
                </div>
                <div className="hint" style={{ padding: '14px 20px 6px' }}>Terms</div>
                <div className="card" style={{ margin: '0 20px' }}>
                  <SegRow label="Starchive fee" value={nc.feeChoice} options={FEE_OPTIONS} onChange={(v) => setNc({ ...nc, feeChoice: v, default_split: v === 'other' ? 0 : 100 - Number(v) })} />
                  {nc.feeChoice === 'other' && <MoneyRow id="ncfee" label="Custom fee" value={nc.feeOther ?? ''} onChange={(v) => { const f = Number(v); setNc({ ...nc, feeOther: v, default_split: f ? 100 - f : 0 }) }} placeholder="0%" prefix="" suffix="%" decimal max={4} />}
                  <SegRow label="Unsold after term" value={nc.unsold_fate} options={[{ v: 'return', label: 'Return' }, { v: 'donate', label: 'Donate' }]} onChange={(v) => setNc({ ...nc, unsold_fate: v })} />
                  <SegRow label="Agreement" value={nc.agreement} options={[{ v: 'signed', label: 'Signed' }, { v: 'not_yet', label: 'Not yet' }]} onChange={(v) => setNc({ ...nc, agreement: v })} />
                </div>
                <div className="hint" style={{ padding: '10px 24px' }}>{mode === 'edit' ? 'Name, phone, email and the split are changed in Consignify first (it is the record), then here. The fee applies to new intakes; pieces already assigned keep the split they were taken in with.' : "Saved to Starchive's records now and created in Consignify once the live store is connected. Consignors are paid out the same way their piece was paid for."}</div>
                {err && <div className="banner" style={{ margin: '0 20px', background: '#d70015' }}>{err}</div>}
              </>
            ) : cur ? (
              <>
                <div className="detail-head"><span className="person__initial">{cur.first_name[0] ?? '?'}</span><b>{consignorName(cur)}</b></div>
                <div className="card review-rows" style={{ margin: '8px 20px' }}>
                  <KV k="Phone" v={cur.phone || '—'} />
                  <KV k="Email" v={cur.email || '—'} />
                  <KV k="Starchive fee" v={`${100 - cur.default_split}% (consignor gets ${cur.default_split}%)`} />
                  <KV k="Unsold after term" v={cur.unsold_fate === 'return' ? 'Return' : 'Donate'} />
                  <KV k="Agreement" v={cur.agreement === 'signed' ? 'Signed' : 'Not yet'} />
                  <KV k="Pieces" v={String(cur.pieces ?? 0)} />
                  <KV k="Consignify" v={cur.in_consignify ? `Linked · ${cur.key}` : `${cur.key} · linked at go-live`} />
                </div>
              </>
            ) : <div className="empty">Pick a consignor to see their details.</div>}
          </div>
          <div className="aside__foot">
            {mode === 'edit'
              ? <div style={{ display: 'flex', gap: 10 }}><button type="button" className="cta cta--center" style={{ flex: 1, background: '#e5e5ea', color: '#000' }} disabled={busy} onClick={() => { setMode('detail'); setErr('') }}>Cancel</button><button type="button" className="cta cta--center" style={{ flex: 2 }} disabled={!ncOk || busy} onClick={save}>{busy ? <Working>Saving</Working> : 'Save changes'}</button></div>
              : mode === 'new'
              ? <button type="button" className="cta cta--center" disabled={!ncOk || busy} onClick={save}>{busy ? <Working>Adding</Working> : picking ? 'Add and use for this intake' : 'Add consignor'}</button>
              : <div style={{ display: 'flex', gap: 10 }}>{cur && !picking && <button type="button" className="cta cta--center" style={{ flex: 1, background: '#e5e5ea', color: '#000' }} onClick={startEdit}>Edit</button>}<button type="button" className="cta cta--center" style={{ flex: 2 }} onClick={() => { setNc({ ...blank }); setMode('new') }}>New consignor</button></div>}
          </div>
        </>
      }
    >
      {picking && <div className="banner">Pick the consignor for this intake, or add a new one.</div>}
      <div className="search">{I.search()}<label htmlFor="cq" style={{ position: 'absolute', left: -9999 }}>Search consignors</label><input id="cq" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or phone" /></div>
      <div className="card">
        {list.map((c) => (
          <button key={c.id} type="button" className={'person' + (c.id === sel && !picking ? ' sel' : '')} onClick={() => pick(c)}>
            <span className="person__initial">{c.first_name[0] ?? '?'}</span>
            <span style={{ flexGrow: 1, minWidth: 0 }}><span className="person__name">{consignorName(c)}</span><span className="person__sub">{100 - c.default_split}% fee{c.phone ? ' · ' + c.phone : ''} · {c.pieces ?? 0} {c.pieces === 1 ? 'piece' : 'pieces'}</span></span>
            {I.chev()}
          </button>
        ))}
        {!ready && list.length === 0 && <Skeleton rows={6} h={64} card={false} />}
        {ready && list.length === 0 && <Empty icon={I.cons(24)} line={q.trim() ? 'No consignor matches.' : 'No consignors yet.'} action="New consignor" onAction={() => setMode('new')} />}
      </div>
    </Page>
  )
}
